import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useJsApiLoader, GoogleMap, Marker, Autocomplete } from '@react-google-maps/api';
import { MapPin, Map as MapIcon, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GOOGLE_LIBS = ['places'];
const DEFAULT_CENTER = { lat: 51.1657, lng: 10.4515 }; // Germany center

/**
 * Hybrid location picker:
 *  - Text input with Google Places Autocomplete (lightweight, typical path)
 *  - Optional "Auf Karte zeigen" button opens a modal with visible map + click-to-pick
 *
 * Props:
 *   value          : string (display name)
 *   onChange       : (name: string, coords: {lat, lng} | null) => void
 *   placeholder    : input placeholder
 *   color          : accent color (for icon)
 *   testId         : data-testid prefix
 *   iconColor      : color for the left MapPin icon
 */
export const LocationPicker = ({
  value, onChange, placeholder, color = '#00d4ff', testId = 'location-picker',
}) => {
  const { t } = useTranslation();
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey || '',
    libraries: GOOGLE_LIBS,
    language: 'de', region: 'DE',
  });

  const autocompleteRef = useRef(null);
  const [coords, setCoords] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [tempMarker, setTempMarker] = useState(null);
  const [tempName, setTempName] = useState('');

  const onAutocompleteLoad = useCallback((ac) => { autocompleteRef.current = ac; }, []);

  const onPlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    if (!place || !place.geometry) return;
    const name = place.name || place.formatted_address || '';
    const c = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    setCoords(c);
    onChange(name, c);
  }, [onChange]);

  const openMap = () => {
    setTempMarker(coords || null);
    setTempName(value || '');
    setMapCenter(coords || DEFAULT_CENTER);
    setShowMap(true);
  };

  const onMapClick = async (e) => {
    const c = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setTempMarker(c);
    // Reverse geocode for display name
    try {
      const geocoder = new window.google.maps.Geocoder();
      const res = await geocoder.geocode({ location: c });
      const best = res.results?.[0];
      setTempName(best?.formatted_address || `${c.lat.toFixed(4)}, ${c.lng.toFixed(4)}`);
    } catch {
      setTempName(`${c.lat.toFixed(4)}, ${c.lng.toFixed(4)}`);
    }
  };

  const confirmMap = () => {
    if (tempMarker) {
      setCoords(tempMarker);
      onChange(tempName, tempMarker);
    }
    setShowMap(false);
  };

  // Fallback plain input if API key is missing or script failed
  if (!apiKey || loadError) {
    return (
      <div style={{ position: 'relative' }}>
        <MapPin size={16} style={{ position: 'absolute', left: 14, top: 14, color }} />
        <input
          data-testid={`${testId}-input`}
          type="text"
          placeholder={placeholder}
          value={value || ''}
          onChange={(e) => onChange(e.target.value, null)}
          style={inputStyle}
        />
      </div>
    );
  }

  return (
    <>
      <div style={{ position: 'relative', display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <MapPin size={16} style={{ position: 'absolute', left: 14, top: 14, color, zIndex: 1 }} />
          {isLoaded ? (
            <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
              <input
                data-testid={`${testId}-input`}
                type="text"
                placeholder={placeholder}
                value={value || ''}
                onChange={(e) => onChange(e.target.value, null)}
                style={inputStyle}
              />
            </Autocomplete>
          ) : (
            <input
              data-testid={`${testId}-input`}
              type="text"
              placeholder={placeholder}
              value={value || ''}
              onChange={(e) => onChange(e.target.value, null)}
              style={inputStyle}
            />
          )}
        </div>
        <button
          type="button"
          onClick={openMap}
          data-testid={`${testId}-map-btn`}
          title={t('locationPicker.openMap', 'Auf Karte zeigen')}
          style={mapBtnStyle}
        >
          <MapIcon size={16} />
        </button>
      </div>

      {/* Map modal */}
      {showMap && isLoaded && (
        <div
          role="dialog"
          data-testid={`${testId}-modal`}
          onClick={() => setShowMap(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 720, background: '#0f172a',
              borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)',
              display: 'flex', flexDirection: 'column', maxHeight: '90vh',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#f1f5f9' }}>
                <MapIcon size={18} style={{ color }} />
                <span style={{ fontSize: 15, fontWeight: 600 }}>
                  {t('locationPicker.title', 'Ort auf Karte wählen')}
                </span>
              </div>
              <button
                onClick={() => setShowMap(false)}
                data-testid={`${testId}-modal-close`}
                style={{
                  padding: 6, borderRadius: 8, cursor: 'pointer',
                  background: 'rgba(255,255,255,0.05)', border: 'none', color: '#94a3b8',
                }}
              >
                <X size={18} />
              </button>
            </div>

            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '55vh' }}
              center={mapCenter}
              zoom={tempMarker ? 13 : 6}
              onClick={onMapClick}
              options={{
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
              }}
            >
              {tempMarker && <Marker position={tempMarker} />}
            </GoogleMap>

            <div style={{ padding: '12px 18px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>
                {tempName || t('locationPicker.clickHint', 'Klicke auf die Karte, um einen Ort zu wählen.')}
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowMap(false)}
                  data-testid={`${testId}-modal-cancel`}
                  style={cancelBtnStyle}
                >
                  {t('common.cancel', 'Abbrechen')}
                </button>
                <button
                  onClick={confirmMap}
                  disabled={!tempMarker}
                  data-testid={`${testId}-modal-confirm`}
                  style={{
                    ...confirmBtnStyle,
                    opacity: tempMarker ? 1 : 0.4,
                    cursor: tempMarker ? 'pointer' : 'not-allowed',
                  }}
                >
                  <Check size={14} /> {t('locationPicker.confirm', 'Übernehmen')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const inputStyle = {
  width: '100%', padding: '12px 16px 12px 40px', borderRadius: 12, fontSize: 15,
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
  color: '#f1f5f9', outline: 'none', boxSizing: 'border-box',
};
const mapBtnStyle = {
  padding: '0 14px', borderRadius: 12, cursor: 'pointer',
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
  color: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const cancelBtnStyle = {
  padding: '9px 18px', borderRadius: 10, cursor: 'pointer',
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
  color: '#94a3b8', fontSize: 13,
};
const confirmBtnStyle = {
  padding: '9px 18px', borderRadius: 10,
  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
  border: 'none', color: '#fff', fontSize: 13, fontWeight: 600,
  display: 'flex', alignItems: 'center', gap: 6,
};
